import { useMediaQuery } from "@mui/system";
import { settings } from "app";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import {
  Datagrid,
  NumberInput,
  EditButton,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
  CreateButton,
  FunctionField,
  AutocompleteInput,
  ShowButton,
} from "react-admin";
import { useNavigate } from "react-router";
import { EmptyList } from "server/components";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import {
  CURRICULUMS,
  CYCLIC_EXAM_EXCEPTIONS,
  MOBILE_BREAKPOINT,
  MODULES,
  StudentServicePath,
  TOPICS,
} from "utils/constants";
import { EducationsExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { MobileListRow } from "features/MobileListRow";
import CloneCurriculumButton from "./components/CloneCurriculumButton";
import CycloneIcon from "@mui/icons-material/Cyclone";
import TopicIcon from "@mui/icons-material/Topic";
import { tenants } from "utils/tenants";
import { administrationRequests } from "api/administrationApi";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { Button, Tooltip } from "@mui/material";

const ListCurriculums = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const canUpdate = checkPermission(getPermissions(), permissions.canCreateCurriculums);
  const navigate = useNavigate();

  const ListActions = hasCreate => {
    return (
      <TopToolbar>
        {hasCreate && <CreateButton />}
        <ExportXlsButton exporter={EducationsExporter(CURRICULUMS)} />
      </TopToolbar>
    );
  };

  const filters = [
    <TextInput
      label="Заглавие"
      source="name"
      style={{ width: 300 }}
    />,
    <NumberInput
      label={"Валиден от"}
      source={"validFrom"}
    />,
    <ReferenceInput
      label="Квалификационна степен"
      source="degreeId"
      reference={administrationRequests.getDegreesSelect()}
    >
      <SelectInput label="Квалификационна степен" />
    </ReferenceInput>,
    <ReferenceInput
      label="Специалност"
      source="specialityId"
      reference={administrationRequests.getSpecialitiesSelect()}
      alwaysOn
    >
      <AutocompleteInput
        label="Специалност"
        filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
        style={{ width: 400 }}
      />
    </ReferenceInput>,
    <TextInput
      label="Срок на обучение"
      source="yearsOfStudy"
      type="number"
    />,
  ];

  return (
    <List
      title="Учебни планове"
      actions={<ListActions hasCreate={canUpdate} />}
      sx={settings.style.expandStyle}
      filters={filters}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => <MobileListRow fields={[{ label: "Заглавие", value: record.name }]} />}
          secondaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Валиден от", value: record.validFrom }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Квалификационна степен",
                    value: (
                      <ReferenceField
                        source="degreeId"
                        reference={administrationRequests.getDegreesSelect()}
                        record={record}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Специалност",
                    value: (
                      <ReferenceField
                        source="specialityId"
                        reference={administrationRequests.getSpecialitiesSelect()}
                        record={record}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={record => (
            <div>
              {record.hasCyclicDisciplines && settings.tenant === tenants.MU && (
                <SecuredComponent permission={permissions.canPreviewCurriculums}>
                  <Tooltip title="Комплексни изпити">
                    <Button
                      startIcon={<CycloneIcon />}
                      onClick={() => navigate(`/${StudentServicePath}${CYCLIC_EXAM_EXCEPTIONS}/${record?.id}`)}
                    />
                  </Tooltip>
                </SecuredComponent>
              )}
              {settings.tenant === tenants.MU && (
                <SecuredComponent permission={permissions.canPreviewCurriculumDisciplineTopics}>
                  <Tooltip title="Теми">
                    <Button
                      startIcon={<TopicIcon />}
                      onClick={() =>
                        navigate(`/${StudentServicePath}${TOPICS}/${CURRICULUMS}/${record?.id}`, {
                          state: { curriculumName: record.name },
                        })
                      }
                    />
                  </Tooltip>
                </SecuredComponent>
              )}
              {settings.tenant === tenants.MU && (
                <SecuredComponent permission={permissions.canPreviewCurriculumModules}>
                  <Tooltip title="Модули">
                    <Button
                      startIcon={<ViewModuleIcon />}
                      onClick={() =>
                        navigate(`/${StudentServicePath}${MODULES}/${CURRICULUMS}/${record?.id}`, {
                          state: { curriculumName: record.name },
                        })
                      }
                    />
                  </Tooltip>
                </SecuredComponent>
              )}
              <SecuredComponent permission={permissions.canCreateCurriculums}>
                <CloneCurriculumButton isMobile />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canUpdateCurriculums}>
                <EditButton label="" />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canPreviewCurriculums}>
                <ShowButton label="" />
              </SecuredComponent>
            </div>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField
            source="name"
            label="Заглавие"
            textAlign="center"
            sortable={false}
          />
          <TextField
            source="validFrom"
            label="Валиден от"
            sortable={false}
          />
          <ReferenceField
            label="Квалификационна степен"
            source="degreeId"
            textAlign="center"
            reference={administrationRequests.getDegreesSelect()}
            sortable={false}
          >
            <TextField
              textAlign="center"
              source="name"
              label="Квалификационна степен"
            />
          </ReferenceField>
          <ReferenceField
            label="Специалност"
            source="specialityId"
            textAlign="center"
            reference={administrationRequests.getSpecialitiesSelect()}
            sortable={false}
          >
            <TextField
              textAlign="center"
              source="name"
              label="Специалност"
            />
          </ReferenceField>
          <FunctionField
            render={rec =>
              rec?.hasCyclicDisciplines &&
              settings.tenant === tenants.MU && (
                <SecuredComponent permission={permissions.canPreviewCurriculums}>
                  <Button
                    startIcon={<CycloneIcon />}
                    onClick={() => navigate(`/${StudentServicePath}${CYCLIC_EXAM_EXCEPTIONS}/${rec?.id}`)}
                  >
                    Комплексни изпити
                  </Button>
                </SecuredComponent>
              )
            }
          />
          <FunctionField
            render={rec =>
              settings.tenant === tenants.MU && (
                <SecuredComponent permission={permissions.canPreviewCurriculumDisciplineTopics}>
                  <Button
                    startIcon={<TopicIcon />}
                    onClick={() =>
                      navigate(`/${StudentServicePath}${TOPICS}/${CURRICULUMS}/${rec?.id}`, {
                        state: { curriculumName: rec.name },
                      })
                    }
                  >
                    Теми
                  </Button>
                </SecuredComponent>
              )
            }
          />
          <FunctionField
            render={rec =>
              settings.tenant === tenants.MU && (
                <SecuredComponent permission={permissions.canPreviewCurriculumModules}>
                  <Button
                    startIcon={<ViewModuleIcon />}
                    onClick={() =>
                      navigate(`/${StudentServicePath}${MODULES}/${CURRICULUMS}/${rec?.id}`, {
                        state: { curriculumName: rec.name },
                      })
                    }
                  >
                    Модули
                  </Button>
                </SecuredComponent>
              )
            }
          />
          <SecuredComponent permission={permissions.canCreateCurriculums}>
            <CloneCurriculumButton />
          </SecuredComponent>
          <SecuredComponent permission={permissions.canUpdateCurriculums}>
            <EditButton />
          </SecuredComponent>
          <SecuredComponent permission={permissions.canPreviewCurriculums}>
            <ShowButton />
          </SecuredComponent>
        </Datagrid>
      )}
    </List>
  );
};

export default ListCurriculums;
